import { FC, useMemo } from "react";
import { NavigationBarStyled } from "./navigation-bar-2.styled";
import { useNavigationBar } from "../../navigation-bar.hook";
import { NavigationBarProps } from "./navigation-bar-2.types";
import Evo from "@sumerkit/icons/evo";
import { House, ShoppingBag, ChatCircle, Package, Clock, Brain } from "@phosphor-icons/react";
import { Logo } from "@dashboard/assets/icons";
import { theme } from "@dashboard/assets/theme";
import NavigationMenuCollapsible from "./navigation-menu-collapsible";
import {
  getWelcomeBasicTrialDaysLeft,
  shouldShowWelcomeBasicTrialCountdown,
} from "@dashboard/constants/welcome-basic-trial.constants";

const NavigationBar: FC<NavigationBarProps> = () => {
  const {
    t,
    plan,
    isNavigationBarCollapsed,
    handleNavigationBarCollapse,
    isActiveItem,
    handleClickItemCheck,
    handleClickAccount,
    handleClickHelp,
    handleClickAiAssistant,
  } = useNavigationBar();

  const welcomeBasicTrialDaysLeft = useMemo(() => {
    if (!shouldShowWelcomeBasicTrialCountdown(plan)) return null;
    return getWelcomeBasicTrialDaysLeft(plan!);
  }, [plan]);

  const collapsibleMenuItems = useMemo(() => [
    {
      id: "home",
      label: "Inicio",
      icon: <House weight="regular" size={18} />,
      link: "/",
    },
    {
      id: "store",
      label: "Tienda",
      icon: <ShoppingBag weight="regular" size={18} />,
      subItems: [
        {
          id: "products",
          label: "Productos",
          link: "/products",
        },
        {
          id: "offers",
          label: "Promociones",
          link: "/offers",
        },
        {
          id: "coupons",
          label: "Cupones",
          link: "/coupons",
        },
        {
          id: "kits",
          label: "Combos",
          link: "/kits",
        },
        {
          id: "personalize-store",
          label: "Personalizar tienda",
          link: "/personalization",
          noCloseMenu: true,
        },
        {
          id: "payment-gateway",
          label: "Métodos de pago",
          link: "/configuration/pasarelas",
          noCloseMenu: true,
        },
        {
          id: "terms-and-conditions",
          label: "Términos y condiciones",
          link: "/configuration/terms-and-conditions",
          noCloseMenu: true,
        },
        {
          id: "mobile",
          label: "Móvil",
          link: "/mobile",
        },
      ],
    },
    {
      id: "sales-channels",
      label: "Ventas y canales",
      icon: <ChatCircle weight="regular" size={18} />,
      subItems: [
        {
          id: "whatsapp",
          label: "Botón de WhatsApp",
          link: "/contact-button",
        },
        {
          id: "google-shopping",
          label: "Google Shopping",
          link: "/google",
        },
        {
          id: "abandoned-carts",
          label: "Carritos abandonados",
          link: "/abandoned-carts",
        },
      ],
    },
    {
      id: "operations",
      label: "Operación",
      icon: <Package weight="regular" size={18} />,
      subItems: [
        {
          id: "orders",
          label: "Pedidos",
          link: "/orders",
        },
        {
          id: "payment-receipts",
          label: "Comprobantes de pago",
          link: "/voucher",
          noCloseMenu: true,
        },
        {
          id: "delivery",
          label: "Envíos",
          link: "/delivery",
        },
        {
          id: "customers",
          label: t("navbar.customers"),
          link: "/customers",
        },
      ],
    },
    {
      id: "seller-chat",
      label: "Dommi IA",
      icon: <Brain weight="regular" size={18} />,
      link: "https://dommi.domun.co/",
      isExternalLink: true,
      highlight: true
    }
  ], [t]);

  return (
    <NavigationBarStyled
      className={isNavigationBarCollapsed ? `navigation-bar__collapse` : ""}
    >
      <header className="navigation-bar-header">
        <Logo svgColor={theme.colors.black} svgHeight={30} svgWidth={80} svgClass="navigation-bar-icon icon-sumer-logo" />
        <button
          className="navigation-bar-toggle"
          onClick={() => handleNavigationBarCollapse(!isNavigationBarCollapsed)}
        >
          <span className="icon-arrow-go-up" />
        </button>
      </header>
      <hr className="navigation-bar-divider" />
      {welcomeBasicTrialDaysLeft !== null && (
        <div className="welcome-basic-trial-banner" role="status">
          <Clock
            className="welcome-basic-trial-banner__icon"
            size={14}
            weight="bold"
            aria-hidden
          />
          <span className="welcome-basic-trial-banner__text">
            {welcomeBasicTrialDaysLeft === 0
              ? t("navbar.welcome_basic_trial_today")
              : welcomeBasicTrialDaysLeft === 1
                ? t("navbar.welcome_basic_trial_one_day")
                : t("navbar.welcome_basic_trial_days", {
                    count: welcomeBasicTrialDaysLeft,
                  })}
          </span>
        </div>
      )}
      {/* Menú con desplegables (incluye Seller Chat) */}
      <NavigationMenuCollapsible
        items={collapsibleMenuItems}
        isNavigationBarCollapsed={isNavigationBarCollapsed}
        onItemClick={handleClickItemCheck}
        isActiveItem={isActiveItem}
        onOpenNavBar={() => handleNavigationBarCollapse(false)}
        t={t}
      />
      <hr className="navigation-bar-divider navigation-bar-divider__full" />
      <ul className="navigation-bar-menu">
        <li className="menu-item__evo navigation-bar-menu-item">
          <button
            className="navigation-bar-menu-link"
            onClick={handleClickAiAssistant}>
            <span className={`icon`}>
              <Evo />
            </span>
            <span className="label">{t("aiAssistant.name")}</span>
          </button>
        </li>
        <li className="navigation-bar-menu-item">
          <button
            className={`navigation-bar-menu-link ${
              isActiveItem("/profile") ? "navigation-bar-menu-link__active" : ""
            }`}
            onClick={handleClickAccount}
          >
            <span className={`icon icon-account-circle-outline`} />
            <div className="label">
              {t("navbar.myAccount")}
              <span
                className={`navigation-bar-plan navigation-bar-plan__${
                  plan?.benefit_plan_name?.toLowerCase() || "free"
                }`}
              >
                {t(`navbar.planTag`, {
                  plan: t(
                    `navbar.plan_${plan?.benefit_plan_name?.toLowerCase()}`
                  ),
                })}
              </span>
            </div>
          </button>
        </li>
        <li className="navigation-bar-menu-item">
          <button
            className="navigation-bar-menu-link"
            onClick={handleClickHelp}
          >
            <span className={`icon icon-talk`} />
            <span className="label">{t("support.help")}</span>
          </button>
        </li>
      </ul>
    </NavigationBarStyled>
  );
};

export default NavigationBar;
